import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ArrowLeft, Save } from 'lucide-react';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { ShortcutButton } from '@/components/keyboard/ShortcutIndicator';
import { useFund, useFundOptions } from '@/hooks/react-query/useFunds';
import { useFundLPATerms } from '@/hooks/react-query/useFundLPATerms';
import { getLegalStructuresForCountry } from '@/constants/legalStructures';
import { LPATermsForm } from '@/components/funds/LPATermsForm';
import { decimalToPercentage } from '@/utils/formatters';
import type { Fund } from '@/types';
import type { FundLPATerms } from '@/types/lpa-terms';
import {
  type FundFormData,
  type BankAccountFormData,
  type FundEntityFormData,
  initialFormData,
} from '../../components/funds/fund-edit/types';
import {
  BankAccountsSection,
  BasicInformationSection,
  FinancialInformationSection,
  FormHelpPanel,
  FundEntitiesSection,
  LegalStructureSection,
  TimelineSection,
  useFundFormSave,
} from '@/components/funds/fund-edit';
import { scrollToFirstError } from '@/utils/form-errors';

function validateFundForm(formData: FundFormData): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!formData.name.trim()) {
    errors.name = 'Fund name is required';
  }

  if (!formData.legal_structure || !formData.legal_structure.trim()) {
    errors.legal_structure = 'Legal structure is required';
  }

  if (!formData.vintage_year || Number.isNaN(Number(formData.vintage_year))) {
    errors.vintage_year = 'Valid vintage year is required';
  }

  const currentYear = new Date().getFullYear();
  const vintageYear = Number(formData.vintage_year);
  if (vintageYear < 1900 || vintageYear > currentYear + 10) {
    errors.vintage_year =
      'Vintage year must be between 1900 and ' + (currentYear + 10);
  }

  if (formData.target_size != null && formData.target_size < 0) {
    errors.target_size = 'Target size cannot be negative';
  }

  if (formData.hard_cap != null && formData.hard_cap < 0) {
    errors.hard_cap = 'Hard cap cannot be negative';
  }

  if (
    formData.fund_term_years &&
    Number.isNaN(Number(formData.fund_term_years))
  ) {
    errors.fund_term_years = 'Fund term must be a valid number';
  }

  if (
    !formData.management_fee_basis_investment ||
    !formData.management_fee_basis_investment.trim()
  ) {
    errors.management_fee_basis_investment =
      'Investment period fee basis is required';
  }

  if (
    !formData.management_fee_basis_post_investment ||
    !formData.management_fee_basis_post_investment.trim()
  ) {
    errors.management_fee_basis_post_investment =
      'Post-investment period fee basis is required';
  }

  return errors;
}

function buildFormData(
  fund: Fund | undefined,
  lpaTerms: FundLPATerms | undefined
): FundFormData {
  if (!fund) return initialFormData;

  return {
    ...initialFormData,
    // Fund fields
    name: fund.name ?? '',
    display_name: fund.display_name ?? '',
    fund_type: fund.fund_type ?? initialFormData.fund_type,
    legal_structure: fund.legal_structure ?? initialFormData.legal_structure,
    domicile_country: fund.domicile_country ?? initialFormData.domicile_country,
    base_currency: fund.base_currency ?? initialFormData.base_currency,
    minimum_prepayment_pool: fund.minimum_prepayment_pool ?? null,
    target_size: fund.target_size ?? null,
    hard_cap: fund.hard_cap ?? null,
    first_closing_date: fund.first_closing_date ?? '',
    final_closing_date: fund.final_closing_date ?? '',
    fund_term_years:
      fund.fund_term_years?.toString() ?? initialFormData.fund_term_years,
    extension_periods:
      fund.extension_periods?.toString() ?? initialFormData.extension_periods,
    vintage_year: fund.vintage_year?.toString() ?? initialFormData.vintage_year,
    status: fund.status ?? initialFormData.status,
    lei_code: fund.lei_code ?? '',
    tax_id: fund.tax_id ?? '',
    coc_number: fund.coc_number ?? '',
    regulatory_regime:
      fund.regulatory_regime ?? initialFormData.regulatory_regime,
    accounting_framework:
      fund.accounting_framework ?? initialFormData.accounting_framework,
    lp_allocation_mode:
      fund.lp_allocation_mode ?? initialFormData.lp_allocation_mode,
    parent_fund_id: fund.parent_fund_id ?? 'none',
    master_fund_id: fund.master_fund_id ?? 'none',
    // LPA Terms - only override if lpaTerms exists
    ...(lpaTerms && {
      management_fee_investment_period: decimalToPercentage(
        lpaTerms.investment_period_fee_rate
      ),
      management_fee_post_investment: decimalToPercentage(
        lpaTerms.post_investment_period_fee_rate
      ),
      management_fee_basis_investment:
        lpaTerms.investment_period_fee_basis_id.toString(),
      management_fee_basis_post_investment:
        lpaTerms.post_investment_period_fee_basis_id.toString(),
      gp_commitment: decimalToPercentage(lpaTerms.gp_commitment),
      waterfall_type: lpaTerms.distribution_waterfall_type_id.toString(),
      hurdle_rate: decimalToPercentage(lpaTerms.hurdle_rate),
      gp_catch_up: decimalToPercentage(lpaTerms.catch_up_rate),
      carried_interest: decimalToPercentage(lpaTerms.carried_interest),
      investment_period_years: (
        lpaTerms.investment_period_duration / 12
      ).toString(),
      fund_costs_cap:
        lpaTerms.fund_costs_cap != null
          ? decimalToPercentage(lpaTerms.fund_costs_cap)
          : '',
    }),
  };
}

function buildBankAccounts(fund: Fund | undefined): BankAccountFormData[] {
  if (!fund?.bank_accounts?.length) return [];

  return fund.bank_accounts.map((account) => ({
    id: account.id,
    account_name: account.account_name,
    account_type: account.account_type,
    bank_name: account.bank_name,
    iban: account.iban ?? '',
    swift_bic: account.swift_bic ?? '',
    account_number: account.account_number ?? '',
    currency: account.currency,
    is_primary: account.is_primary,
  }));
}

function buildFundEntities(fund: Fund | undefined): FundEntityFormData[] {
  if (!fund?.fund_entities?.length) return [];

  return fund.fund_entities.map((entity) => ({
    id: entity.id,
    entity_name: entity.entity_name,
    role: entity.role,
    jurisdiction: entity.jurisdiction,
    registration_number: entity.registration_number ?? '',
    iban: entity.iban ?? '',
  }));
}

export default function FundFormPage() {
  const { fundId } = useParams<{ fundId?: string }>();

  // Create mode - no fundId in URL
  if (!fundId) {
    return (
      <FundForm
        key="new"
        fundId={undefined}
        initialFormData={initialFormData}
        initialBankAccounts={[]}
      />
    );
  }

  // Edit mode - delegate to separate component that calls hooks
  return <FundFormPageEdit fundId={fundId} />;
}

// Separate component for edit mode - hooks are always called here
function FundFormPageEdit({ fundId }: { fundId: string }) {
  const { data: fund, isLoading: fundLoading } = useFund(fundId);
  const { data: existingLPATerms, isLoading: lpaLoading } =
    useFundLPATerms(fundId);

  // Wait for data before rendering the form
  if (fundLoading || lpaLoading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-64"></div>
          <div className="h-4 bg-muted rounded w-96"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  // Data is ready - render form with correct initial values
  return (
    <FundForm
      key={fundId}
      fundId={fundId}
      initialFormData={buildFormData(fund, existingLPATerms)}
      initialBankAccounts={buildBankAccounts(fund)}
      initialFundEntities={buildFundEntities(fund)}
    />
  );
}

interface FundFormProps {
  fundId?: string;
  initialFormData: FundFormData;
  initialBankAccounts: BankAccountFormData[];
  initialFundEntities?: FundEntityFormData[];
}

function FundForm({
  fundId,
  initialFormData: initialData,
  initialBankAccounts,
  initialFundEntities = [],
}: FundFormProps) {
  const navigate = useNavigate();
  const isEditing = !!fundId;

  const { data: availableFunds = [] } = useFundOptions();

  // State initialized directly from props - works because parent waits for data
  const [formData, setFormData] = useState<FundFormData>(initialData);
  const [bankAccounts, setBankAccounts] =
    useState<BankAccountFormData[]>(initialBankAccounts);
  const [fundEntities, setFundEntities] =
    useState<FundEntityFormData[]>(initialFundEntities);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { save, isSaving } = useFundFormSave({
    fundId,
    isEditing,
    initialBankAccounts,
    initialFundEntities,
    setErrors,
  });

  const handleCancel = () => {
    if (isEditing && fundId) {
      navigate(`/app/funds/${fundId}`);
    } else {
      navigate('/app/funds');
    }
  };

  const handleSave = () => {
    const newErrors = validateFundForm(formData);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      scrollToFirstError(newErrors);
      return;
    }

    save(formData, bankAccounts, fundEntities);
  };

  // Keyboard shortcuts
  useKeyboardShortcuts('fund-form', [
    {
      key: 'Escape',
      description: 'Cancel and go back',
      action: handleCancel,
      category: 'Form Actions',
    },
    {
      key: 's',
      description: 'Save fund',
      action: handleSave,
      category: 'Form Actions',
      modifiers: { ctrl: true },
    },
  ]);

  // Filter available funds to exclude the current fund when editing
  const filteredAvailableFunds =
    isEditing && fundId
      ? availableFunds.filter((f) => f.id !== fundId)
      : availableFunds;

  const handleInputChange = (
    field: keyof FundFormData,
    value: string | number | null
  ) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };

      // Reset legal structure when country changes
      if (field === 'domicile_country' && typeof value === 'string') {
        const structures = getLegalStructuresForCountry(value);
        if (structures.length > 0) {
          updated.legal_structure = structures[0].value;
        }
      }

      return updated;
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  // Bank account management
  const addBankAccount = () => {
    const newAccount: BankAccountFormData = {
      id: `bank-${Date.now()}`,
      account_name: '',
      account_type: 'operating',
      bank_name: '',
      iban: '',
      swift_bic: '',
      account_number: '',
      currency: formData.base_currency,
      is_primary: bankAccounts.length === 0,
    };
    setBankAccounts([...bankAccounts, newAccount]);
  };

  const updateBankAccount = (
    index: number,
    field: keyof BankAccountFormData,
    value: string | boolean
  ) => {
    const updated = [...bankAccounts];
    updated[index] = { ...updated[index], [field]: value };
    setBankAccounts(updated);
  };

  const removeBankAccount = (index: number) => {
    const updated = bankAccounts.filter((_, i) => i !== index);
    setBankAccounts(updated);
  };

  const setPrimaryBankAccount = (index: number) => {
    const updated = bankAccounts.map((account, i) => ({
      ...account,
      is_primary: i === index,
    }));
    setBankAccounts(updated);
  };

  // Fund entity management
  const addFundEntity = () => {
    const newEntity: FundEntityFormData = {
      id: `entity-${Date.now()}`,
      entity_name: '',
      role: 'gp',
      jurisdiction: formData.domicile_country,
      registration_number: '',
      iban: '',
    };
    setFundEntities([...fundEntities, newEntity]);
  };

  const updateFundEntity = (
    index: number,
    field: keyof FundEntityFormData,
    value: string
  ) => {
    const updated = [...fundEntities];
    updated[index] = { ...updated[index], [field]: value };
    setFundEntities(updated);
  };

  const removeFundEntity = (index: number) => {
    const updated = fundEntities.filter((_, i) => i !== index);
    setFundEntities(updated);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-1">
        {/* Back navigation - only in create mode (edit mode uses layout breadcrumb) */}
        {!isEditing && (
          <button
            onClick={handleCancel}
            className="flex items-center gap-1.5 self-start text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Funds
          </button>
        )}

        {/* Title + Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
              {isEditing ? 'Edit Fund' : 'Create New Fund'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isEditing
                ? 'Update fund settings and information'
                : 'Set up a new fund with detailed configuration'}
            </p>
          </div>
          <ShortcutButton
            shortcut={{ key: 's', modifiers: { ctrl: true } }}
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-brand text-brand-foreground rounded-md hover:bg-brand/90 transition-colors disabled:opacity-50 flex-shrink-0"
          >
            {isSaving ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Fund
              </>
            )}
          </ShortcutButton>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          <BasicInformationSection
            formData={formData}
            errors={errors}
            availableFunds={filteredAvailableFunds}
            onInputChange={handleInputChange}
          />

          <LegalStructureSection
            formData={formData}
            errors={errors}
            onInputChange={handleInputChange}
          />

          <FinancialInformationSection
            formData={formData}
            savedLpAllocationMode={initialData.lp_allocation_mode}
            errors={errors}
            onInputChange={handleInputChange}
          />

          <TimelineSection
            formData={formData}
            errors={errors}
            onInputChange={handleInputChange}
          />

          {/* LPA Terms */}
          <LPATermsForm
            formData={{
              management_fee_investment_period:
                formData.management_fee_investment_period,
              management_fee_post_investment:
                formData.management_fee_post_investment,
              management_fee_basis_investment:
                formData.management_fee_basis_investment,
              management_fee_basis_post_investment:
                formData.management_fee_basis_post_investment,
              gp_commitment: formData.gp_commitment,
              waterfall_type: formData.waterfall_type,
              hurdle_rate: formData.hurdle_rate,
              gp_catch_up: formData.gp_catch_up,
              carried_interest: formData.carried_interest,
              investment_period_years: formData.investment_period_years,
              fund_costs_cap: formData.fund_costs_cap,
              base_currency: formData.base_currency,
            }}
            onInputChange={handleInputChange}
            errors={errors}
          />

          {/* Bank Accounts - Only show in edit mode */}
          {isEditing && (
            <BankAccountsSection
              bankAccounts={bankAccounts}
              onAdd={addBankAccount}
              onUpdate={updateBankAccount}
              onRemove={removeBankAccount}
              onSetPrimary={setPrimaryBankAccount}
            />
          )}

          {/* Fund Entities - Only show in edit mode */}
          {isEditing && (
            <FundEntitiesSection
              fundEntities={fundEntities}
              onAdd={addFundEntity}
              onUpdate={updateFundEntity}
              onRemove={removeFundEntity}
            />
          )}
        </div>

        {/* Side Panel */}
        <div className="space-y-6">
          <FormHelpPanel />
        </div>
      </div>
    </div>
  );
}
